import SMimg from '../../images/StaffManager.svg';
import HODimg from '../../images/HeadofDepartment.svg';
import CCimg from '../../images/CredentialingCommittee.svg';
import COSimg from '../../images/ChiefofStaff.svg';
import SAimg from '../../images/SystemAdmin.svg';
import CAPManager from '../../images/CAPManagerSmallLogo.png';
import MDManager from '../../images/MDManager.png';
import PNPManager from '../../images/PNPManager.png';
import EDUSmart from '../../images/EDUSmart.png';
import SureShield from '../../images/SureShieldGreen.png';
import TimeSmartLogo from '../../images/timeSmartAILogo.png';

export const DEMO_PRODUCTS = {
  CAP_MANAGER: {
    key: 'CAP_MANAGER',
    title: 'CAPSmart',
    titleParts: { lead: 'CAP', accent: 'Smart' },
    subtitle: 'Credentialing & Privileging',
    description:
      'Explore staff workflows, applications, documents, and privileging decisions across CAPSmart workspaces.',
    image: CAPManager,
    accent: '#06617A',
    roleSource: 'roles',
    workspaces: [
      {
        role: 'Staff Manager',
        label: 'Staff Manager',
        icon: SMimg,
        description:
          'Manage staff records, send applications, and verify applications & documents throughout the credentialing cycle.',
        guidance:
          'In this demo you will work as a Staff Manager inside CAPSmart. You will see the applications list, open staff records, review supporting documents, and walk through how applications move through credentialing. Use this workspace to explore day-to-day intake, verification, and follow-up tasks that keep privileging data complete and current.',
        route: '/applications',
      },
      {
        role: 'Department Head',
        label: 'Department Head',
        icon: HODimg,
        description:
          "Review your department's staff including locums, assess applicants, and keep departmental privileging on track.",
        guidance:
          'In this demo you will work as a Department Head. You will review applicants and staff associated with your department, including locums, and see how departmental leaders evaluate applications before they advance. Focus on the decisions and visibility a department head needs to keep privileging aligned with clinical coverage.',
        route: '/applications',
      },
      {
        role: 'Credentialing Committee',
        label: 'Credentialing Committee',
        icon: CCimg,
        description:
          'Review staff data on applications and documents, then approve applications as part of committee workflows.',
        guidance:
          'In this demo you will work from the Credentialing Committee perspective. You will review application packets, supporting documents, and committee-ready staff data, then see how approvals are recorded as part of the formal credentialing process.',
        route: '/applications',
      },
      {
        role: 'Credentialing Committee User',
        label: "Staff Manager's Credentialing Committee Workspace",
        icon: CCimg,
        description:
          'Support credentialing committee reviews from the staff-manager workspace perspective.',
        guidance:
          'In this demo you will use the Staff Manager credentialing committee workspace. You will see how committee-supporting users prepare and follow applications through review, bridging operational staff-manager tasks with committee decision-making.',
        route: '/applications',
      },
      {
        role: 'Chief Of Staff',
        label: 'Chief Of Staff',
        icon: COSimg,
        description:
          'Finalize privileging decisions and override temporary appointments when clinical leadership action is required.',
        guidance:
          'In this demo you will work as Chief of Staff. You will see final privileging decisions, leadership overrides for temporary appointments, and the checkpoints where clinical authority closes out credentialing actions.',
        route: '/applications',
      },
      {
        role: 'Entity Sys Admin',
        label: 'System Administrator',
        icon: SAimg,
        description:
          'Configure entity and site settings that power CAPSmart across your organization.',
        guidance:
          'In this demo you will enter the System Administrator portal. You will explore entity and site configuration that powers CAPSmart—how organizational setup, sites, and admin controls underpin credentialing workflows for the rest of the users.',
        route: '/entitySitePortal',
      },
    ],
  },
  MD_MANAGER: {
    key: 'MD_MANAGER',
    title: 'MD Manager',
    titleParts: { lead: 'MD', accent: 'Manager' },
    subtitle: 'Medical Directives',
    description:
      'Walk through drafting, acknowledgement, review, and publishing of medical directives.',
    image: MDManager,
    accent: '#1F7A5C',
    roleSource: 'mdRoles',
    workspaces: [
      {
        role: 'MD Librarian',
        label: 'MD Librarian',
        icon: SMimg,
        description:
          'Create or update Medical Directives, adjust workflows, and manage attestation groups.',
        guidance:
          'In this demo you will work as an MD Librarian. You will browse medical directives, update content and workflows, and see how attestation groups are managed so directives move cleanly from draft to published practice.',
        route: '/mdManager',
      },
      {
        role: 'Author / Owner',
        label: 'Author / Owner',
        icon: SAimg,
        description:
          'Draft or update Medical Directives and keep content accurate and current before review.',
        guidance:
          'In this demo you will work as an Author / Owner. You will draft or revise medical directive content and prepare it for acknowledgement and approval, focusing on keeping clinical guidance accurate before it advances.',
        route: '/mdManager',
      },
      {
        role: 'Acknowledger',
        label: 'Acknowledger',
        icon: SMimg,
        description:
          'Review MDs and confirm they align with your department’s practices before they move forward.',
        guidance:
          'In this demo you will work as an Acknowledger. You will review medical directives queued for departmental confirmation and acknowledge that content aligns with local practice before it continues through the approval path.',
        route: '/mdManager/manageAcknowledgement',
      },
      {
        role: 'Reviewer / Approver',
        label: 'Reviewer / Approver',
        icon: SMimg,
        description:
          'Review MDs for accuracy and approve them for publishing and attestation.',
        guidance:
          'In this demo you will work as a Reviewer / Approver. You will examine directives ready for sign-off, confirm accuracy, and approve them for publishing and attestation so clinical staff can act on current guidance.',
        route: '/mdManager/manageSignOff',
      },
    ],
  },
  PNP_MANAGER: {
    key: 'PNP_MANAGER',
    title: 'P&P Manager',
    titleParts: { lead: 'P&P', accent: 'Manager', pnp: true },
    subtitle: 'Policies & Procedures',
    description:
      'Demo policy authoring, acknowledgement, approval, and attestation cycles in P&P Manager.',
    image: PNPManager,
    accent: '#8A5A1A',
    roleSource: 'pnpRoles',
    workspaces: [
      {
        role: 'P&P Manager',
        label: 'P&P Manager',
        icon: SMimg,
        description:
          'Create and manage policy documents, update workflows, and coordinate reviews.',
        guidance:
          'In this demo you will work as a P&P Manager. You will manage policy documents, adjust review workflows, and coordinate how policies move from draft through acknowledgement, approval, and attestation.',
        route: '/pnpManager',
      },
      {
        role: 'Author / Owner',
        label: 'Author / Owner',
        icon: SAimg,
        description:
          'Draft or update Policy & Procedures and ensure they are accurate and current.',
        guidance:
          'In this demo you will work as an Author / Owner. You will draft or update policy and procedure content and prepare it for validation and approval, keeping organizational guidance current.',
        route: '/pnpManager',
      },
      {
        role: 'Acknowledger',
        label: 'Acknowledger',
        icon: SMimg,
        description:
          'Validate P&P content and confirm details before they move to approval.',
        guidance:
          'In this demo you will work as an Acknowledger. You will validate policy content queued for confirmation and acknowledge accuracy before policies advance to formal approval.',
        route: '/pnpManager/manageAcknowledgement',
      },
      {
        role: 'Reviewer / Approver',
        label: 'Reviewer / Approver',
        icon: SMimg,
        description:
          'Review policy drafts for accuracy and approve them for publishing.',
        guidance:
          'In this demo you will work as a Reviewer / Approver. You will review policy drafts awaiting sign-off and approve them for publishing so teams can attest to the latest procedures.',
        route: '/pnpManager/manageSignOff',
      },
    ],
  },
  CONTRACT_MANAGER: {
    key: 'CONTRACT_MANAGER',
    title: 'ContractSmart',
    titleParts: { lead: 'Contract', accent: 'Smart' },
    subtitle: 'Contract Management',
    description:
      'Explore contract creation, viewing, and reporting workspaces in ContractSmart.',
    image: CAPManager,
    accent: '#3D4F6F',
    roleSource: 'contractManagerRoles',
    workspaces: [
      {
        role: 'Contract Manager',
        label: 'Contract Manager',
        icon: SMimg,
        description: 'Create or update contracts and manage the full contract lifecycle.',
        guidance:
          'In this demo you will work as a Contract Manager. You will explore contract records, create or update agreements, and see how ContractSmart supports the full lifecycle from intake through ongoing management.',
        route: '/contractSmart',
      },
      {
        role: 'Contract Viewer',
        label: 'Contract Viewer',
        icon: SMimg,
        description: 'View a specific collection of contracts with read-focused access.',
        guidance:
          'In this demo you will work as a Contract Viewer. You will open a curated set of contracts with read-focused access and see how stakeholders review agreement details without changing the underlying records.',
        route: '/contractSmart',
      },
      {
        role: 'Report Viewers',
        label: 'Report Viewers',
        icon: SMimg,
        description: 'Open contract reports and analytics for leadership visibility.',
        guidance:
          'In this demo you will work as a Report Viewer. You will open contract reports and analytics that give leadership visibility into portfolio status, obligations, and operational trends.',
        route: '/contractSmart',
      },
      {
        role: 'Sys Admin',
        label: 'Sys Admin',
        icon: SAimg,
        description: 'Administer ContractSmart configuration and supporting setup.',
        guidance:
          'In this demo you will work as a ContractSmart Sys Admin. You will explore configuration and supporting setup that controls how contract workspaces, access, and reporting are organized for the organization.',
        route: '/contractSmart',
      },
    ],
  },
  LMS_MANAGER: {
    key: 'LMS_MANAGER',
    title: 'EDUSmart',
    titleParts: { lead: 'EDU', accent: 'Smart' },
    subtitle: 'Learning & Competency',
    description:
      'Launch EDUSmart to assign, track, and complete training and competency modules.',
    image: EDUSmart,
    accent: '#2F6B9A',
    roleSource: 'lmsRoles',
    workspaces: [],
    externalLms: true,
    lmsGuidance: {
      label: 'EDUSmart Learning',
      guidance:
        'In this demo you will launch EDUSmart, the learning and competency workspace. You will see how training and competency modules are assigned, tracked, and completed outside the main CAPSmart shell via secure SSO into the learning experience.',
    },
  },
  INTEGRITY_SHIELD: {
    key: 'INTEGRITY_SHIELD',
    title: 'IntegrityShield',
    titleParts: { lead: 'Integrity ', accent: 'Shield' },
    subtitle: 'Integrity Monitoring',
    description:
      'Explore Integrity Shield monitoring experiences for sanctions, conflict of interest, and licensure credentials.',
    image: SureShield,
    accent: '#7A3E4B',
    roleSource: null,
    externalIntegrityShield: true,
    workspaces: [
      {
        id: 'sanctions',
        role: 'Sanctions and Exclusions',
        label: 'Sanctions and Exclusions',
        icon: SureShield,
        integrityModule: 'EXCLUSIONS',
        description:
          'Monitor sanctions and exclusions screening results and follow up on integrity matches.',
        guidance:
          'In this demo you will open Integrity Shield for Sanctions and Exclusions. You will explore screening results, review matches, and see how teams investigate and resolve integrity findings in a secure external experience.',
      },
      {
        id: 'conflict-of-interest',
        role: 'Conflict of Interest',
        label: 'Conflict of Interest',
        icon: SureShield,
        integrityModule: 'COI',
        description:
          'Review conflict of interest disclosures and monitoring workflows in Integrity Shield.',
        guidance:
          'In this demo you will open Integrity Shield for Conflict of Interest. You will see how disclosures and related monitoring are tracked so organizations can identify and manage potential conflicts.',
      },
      {
        id: 'licensure',
        role: 'Licensure and Credentials Monitoring',
        label: 'Licensure and Credentials Monitoring',
        icon: SureShield,
        integrityModule: 'LICENSURE',
        description:
          'Track licensure and credentials monitoring alerts across your provider population.',
        guidance:
          'In this demo you will open Integrity Shield for Licensure and Credentials Monitoring. You will explore license and credential monitoring signals so teams can follow up on expirations, status changes, and related alerts.',
      },
    ],
  },
  PROVIDER_PORTAL: {
    key: 'PROVIDER_PORTAL',
    title: 'Provider Portal',
    titleParts: { lead: 'Provider ', accent: 'Portal' },
    subtitle: 'Applicant Experience',
    description:
      'Explore the Provider Portal applicant dashboard for tasks, applications, calendar, and attestations.',
    image: CAPManager,
    accent: '#06617A',
    roleSource: null,
    skipRoleFilter: true,
    workspaces: [
      {
        id: 'applicant-dashboard',
        role: 'Provider',
        label: 'Provider Portal',
        icon: CAPManager,
        description:
          'Open the applicant dashboard to review tasks, current applications, calendar items, and medical directive attestations.',
        guidance:
          'In this demo you will work in the Provider Portal (Applicant Dashboard). You will see how providers manage tasks, track applications, use the calendar, and complete medical directive attestations from their self-service experience.',
        route: '/ApplicantDashboard',
      },
    ],
  },
  TIMESMART: {
    key: 'TIMESMART',
    title: 'TimeSmart',
    titleParts: { lead: 'Time', accent: 'Smart' },
    subtitle: 'Time & Attendance',
    description:
      'Launch TimeSmart via SSO for single-site or multi-site tenant experiences.',
    image: TimeSmartLogo,
    accent: '#5A6B3D',
    roleSource: null,
    externalTimeSmart: true,
    workspaces: [
      {
        id: 'single-site',
        role: 'Single Site Tenant',
        label: 'Single Site Tenant',
        icon: TimeSmartLogo,
        siteType: 'SINGLE',
        description:
          'Open TimeSmart for a single-site tenant via secure SSO.',
        guidance:
          'In this demo you will launch TimeSmart for a Single Site Tenant. You will be signed in through SSO and land in the single-site TimeSmart experience.',
      },
      {
        id: 'multi-site',
        role: 'Multi Site Tenant',
        label: 'Multi Site Tenant',
        icon: TimeSmartLogo,
        siteType: 'MULTI',
        description:
          'Open TimeSmart for a multi-site tenant via secure SSO.',
        guidance:
          'In this demo you will launch TimeSmart for a Multi Site Tenant. You will be signed in through SSO and land in the multi-site TimeSmart experience.',
      },
    ],
  },
};

export const getProduct = (workspaceKey) =>
  DEMO_PRODUCTS[(workspaceKey || '').toUpperCase()] || null;
